import { useMemo } from 'react';
import { ISelectOption } from '@creditflowresearch/uilib/Select';
import {
  useCorporateBondScreenerSidebarFilterContext,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import {
  createUseMultiSelectFilter,
} from '@/creditflowresearch/application/BondScreener/createUseMultiSelectFilter';
import useExcludeToggle from '@/creditflowresearch/ui/common/hooks/sidebar/useExcludeToggle';
import useGetCurrencies from '@/creditflowresearch/application/BondScreener/useGetCurrencies';

export const PINNED_CURRENCY_IDS = [
  '96e6bf40-3b92-4501-943d-282deee59a23',
  'b409fa81-54d0-4906-9d1e-4b6dbc3d0187',
  '03f15dd6-b23e-4ab3-b302-89c499aca4fd',
];

const useMultiSelectFilter = createUseMultiSelectFilter(useCorporateBondScreenerSidebarFilterContext);

const useCurrencySection = () => {
  const { filters } = useCorporateBondScreenerSidebarFilterContext();
  const currencies = useGetCurrencies();

  const { isExcluded, activeKey, onToggleExclude } = useExcludeToggle({
    includeKey: 'includedCurrencyIds',
    excludeKey: 'excludedCurrencyIds',
    stateKey: 'isCurrencyExcluded',
    useFilterContext: useCorporateBondScreenerSidebarFilterContext,
  });

  const boundaryIds = useMemo(() => new Set(filters?.currencyIds ?? []), [filters?.currencyIds]);

  const options: ISelectOption[] = useMemo(() => {
    const optionsById = new Map(
      currencies
        .filter((currency) => boundaryIds.size === 0 || boundaryIds.has(currency.id))
        .map((currency) => [currency.id, { label: `${ currency.name } (${ currency.displayName })`, value: currency.id }]),
    );

    const pinnedSet = new Set(PINNED_CURRENCY_IDS);
    const pinned = PINNED_CURRENCY_IDS.map((id) => optionsById.get(id)).filter(Boolean);
    const rest = [...optionsById.values()].filter((opt) => !pinnedSet.has(String(opt.value)));

    return [...pinned, ...rest];
  }, [currencies, boundaryIds]);

  const { selectedValues, onChange } = useMultiSelectFilter({
    filterKey: activeKey,
    options,
  });

  return {
    options,
    selectedValues,
    onChange,
    isExcludedEnabled: isExcluded,
    onChangeExclude: onToggleExclude,
  };
};

export default useCurrencySection;
